<template>
  <nav class="navbar navbar-expand-lg navbar-dark bg-dark">
    <div class="container-fluid">
      <!-- Brand -->
      <a class="navbar-brand" href="#">
        <i class="fas fa-chart-line me-2"></i>
        <span class="brand-text">{{ $t('header.brand') }}</span>
      </a>
      
      <!-- Mobile menu toggle -->
      <button 
        class="navbar-toggler d-lg-none" 
        type="button" 
        @click="toggleMobileMenu"
        :class="{ 'active': isMobileMenuOpen }"
      >
        <span class="navbar-toggler-icon"></span>
      </button>
      
      <!-- Desktop Navigation - Bootstrap native -->
      <div class="collapse navbar-collapse d-none d-lg-block" v-if="isAuthenticated">
        <div class="navbar-nav me-auto">
          <router-link to="/dashboard" class="nav-link" @click="handleNavClick">
            <i class="fas fa-tachometer-alt me-1"></i>{{ $t('header.dashboard') }}
          </router-link>
          <router-link to="/products" class="nav-link" @click="handleNavClick">
            <i class="fas fa-box me-1"></i>{{ $t('header.products') }}
          </router-link>
          <router-link to="/compare" class="nav-link" @click="handleNavClick">
            <i class="fas fa-balance-scale me-1"></i>{{ $t('header.compare') }}
          </router-link>
          <!-- <router-link to="/alerts" class="nav-link" @click="handleNavClick">
            <i class="fas fa-bell me-1"></i>Alerts
          </router-link> -->
          <router-link to="/review-analytics" class="nav-link" @click="handleNavClick">
            <i class="fas fa-star me-1"></i>{{ $t('header.reviewAnalytics') }}
          </router-link>
          <router-link v-if="showScraperTab" to="/scraper" class="nav-link" @click="handleNavClick">
            <i class="fas fa-robot me-1"></i>{{ $t('header.scraper') }}
          </router-link>
          <router-link to="/intelligence-analysis" class="nav-link" @click="handleNavClick">
            <i class="fas fa-brain me-1"></i>{{ $t('header.intelligenceAnalysis') }}
          </router-link>
        </div>
        
        <!-- Right side info for desktop -->
        <div class="navbar-nav">
          <!-- Language Switcher for Desktop -->
          <div class="nav-item d-flex align-items-center me-3">
            <select v-model="locale" class="form-select form-select-sm">
              <option value="en">English</option>
              <option value="ja">日本語</option>
              <option value="zh">中文</option>
            </select>
          </div>
          <!-- Last Update Time -->
          <div class="nav-item d-flex align-items-center me-3">
            <span class="navbar-text text-muted small">
              <i class="fas fa-clock me-1"></i>
              {{ $t('header.lastUpdate') }}{{ lastUpdateTime }}
            </span>
          </div>
          
          <!-- User Info -->
          <div class="nav-item d-flex align-items-center">
            <span class="navbar-text">
              <i class="fas fa-user me-1"></i>{{ currentUser ? currentUser.username : $t('header.guest') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    
    <!-- Mobile Sliding Menu -->
    <div class="mobile-menu d-lg-none" :class="{ 'show': isMobileMenuOpen }">
      <div class="mobile-menu-content">
        <!-- Mobile User Info -->
        <div class="mobile-user-info" v-if="isAuthenticated">
          <div class="user-section">
            <i class="fas fa-user me-2"></i>
            {{ currentUser ? currentUser.username : $t('header.guest') }}
          </div>
          <div class="update-section">
            <i class="fas fa-clock me-2"></i>
            {{ $t('header.lastUpdate') }}{{ lastUpdateTime }}
          </div>
        </div>
        <!-- Language Switcher for Mobile -->
        <div class="mobile-user-info">
          <select v-model="locale" class="form-select form-select-sm">
            <option value="en">English</option>
            <option value="ja">日本語</option>
            <option value="zh">中文</option>
          </select>
        </div>
        
        <!-- Mobile Navigation Links -->
        <div class="mobile-nav-links">
          <router-link to="/dashboard" class="mobile-nav-link" @click="handleMobileNavClick">
            <i class="fas fa-tachometer-alt me-2"></i>{{ $t('header.dashboard') }}
          </router-link>
          <router-link to="/products" class="mobile-nav-link" @click="handleMobileNavClick">
            <i class="fas fa-box me-2"></i>{{ $t('header.products') }}
          </router-link>
          <router-link to="/compare" class="mobile-nav-link" @click="handleMobileNavClick">
            <i class="fas fa-balance-scale me-2"></i>{{ $t('header.compare') }}
          </router-link>
          <!-- <router-link to="/alerts" class="mobile-nav-link" @click="handleMobileNavClick">
            <i class="fas fa-bell me-2"></i>Alerts
          </router-link> -->
          <router-link to="/review-analytics" class="mobile-nav-link" @click="handleMobileNavClick">
            <i class="fas fa-star me-2"></i>{{ $t('header.reviewAnalytics') }}
          </router-link>
          <router-link v-if="showScraperTab" to="/scraper" class="mobile-nav-link" @click="handleMobileNavClick">
            <i class="fas fa-robot me-2"></i>{{ $t('header.scraper') }}
          </router-link>
          <router-link to="/intelligence-analysis" class="mobile-nav-link" @click="handleMobileNavClick">
            <i class="fas fa-brain me-2"></i>{{ $t('header.intelligenceAnalysis') }}
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mobile Menu Overlay -->
    <div 
      class="mobile-menu-overlay d-lg-none" 
      :class="{ 'show': isMobileMenuOpen }"
      @click="closeMobileMenu"
    ></div>
  </nav>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'AppHeader',
  props: {
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      default: null
    },
    lastUpdateTime: {
      type: String,
      default: '無資料'
    }
  },
  setup() {
    const { locale } = useI18n();
    const isMobileMenuOpen = ref(false);

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
      // Prevent body scroll when menu is open
      if (isMobileMenuOpen.value) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    };

    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false;
      document.body.style.overflow = '';
    };

    const handleNavClick = (event) => {
      // 添加點擊動畫效果
      const link = event.currentTarget;
      link.style.animation = 'none';
      
      // 強制重繪
      link.offsetHeight;
      
      // 添加點擊動畫
      link.style.animation = 'clickAnimation 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      
      // 清理動畫
      setTimeout(() => {
        link.style.animation = '';
      }, 300);
    };

    const handleMobileNavClick = (event) => {
      // 移動端點擊動畫
      const link = event.currentTarget;
      link.style.animation = 'mobileClickAnimation 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      
      // 延遲關閉選單，讓動畫完成
      setTimeout(() => {
        closeMobileMenu();
        link.style.animation = '';
      }, 200);
    };

    // Close menu on window resize to desktop size
    const handleResize = () => {
      if (window.innerWidth >= 992) { // Bootstrap lg breakpoint
        closeMobileMenu();
      }
    };

    // Close menu on escape key
    const handleKeydown = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen.value) {
        closeMobileMenu();
      }
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
      document.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeydown);
      // Clean up body overflow style
      document.body.style.overflow = '';
    });

    const showScraperTab = ref(false); // New reactive property

    return {
      isMobileMenuOpen,
      toggleMobileMenu,
      closeMobileMenu,
      handleNavClick,
      handleMobileNavClick,
      locale,
      showScraperTab, // Expose to template
    };
  }
};
</script>

<style scoped>
/* Base navbar styles */
.navbar {
  padding: 0.5rem 0;
}

/* 桌面版使用 Bootstrap 原生佈局 */

.navbar-brand {
  font-weight: 600;
  font-size: 1.1rem;
  padding: 0.5rem 1rem;
}

.brand-text {
  display: inline;
}

/* Navigation links */
.nav-link {
  color: #adb5bd !important;
  font-weight: 500;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.5rem 1rem !important;
  display: flex;
  align-items: center;
  white-space: nowrap;
  position: relative;
  border-radius: 6px;
  margin: 0 2px;
  transform: translateY(0);
  overflow: hidden;
}



/* 添加背景過渡效果 */
.nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: -1;
  border-radius: 6px;
}

/* Hover effect */
.nav-link:hover {
  color: #fff !important;
  background-color: #495057;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Active state - more prominent */
.nav-link.router-link-active {
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
  transform: translateY(-2px);
  font-weight: 600;
  animation: activateLink 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Active state background slide */
.nav-link.router-link-active::before {
  left: 0;
}



/* 激活動畫 */
@keyframes activateLink {
  0% {
    transform: translateY(0) scale(1);
    box-shadow: none;
  }
  50% {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 123, 255, 0.6);
  }
  100% {
    transform: translateY(-2px) scale(1);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
  }
}

/* 點擊動畫 */
@keyframes clickAnimation {
  0% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-1px) scale(0.98);
  }
  100% {
    transform: translateY(0) scale(1);
  }
}

/* 頁面切換時的過渡動畫 */
.nav-link.switching {
  animation: switchOut 0.2s ease-in-out;
}

@keyframes switchOut {
  0% {
    opacity: 1;
    transform: translateY(-2px) scale(1);
  }
  100% {
    opacity: 0.7;
    transform: translateY(0) scale(0.95);
  }
}

/* Pulse animation for active state */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 0.3;
  }
}

/* Icon animation in active state */
.nav-link.router-link-active i {
  animation: bounce 0.6s ease-in-out;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-3px);
  }
  60% {
    transform: translateY(-1px);
  }
}

/* Navbar info section */
.navbar-text {
  font-size: 0.85rem;
  color: #6c757d !important;
  white-space: nowrap;
  padding: 0.5rem 0.75rem;
}

.navbar-text i {
  color: #28a745;
}

/* 為右側資訊添加分隔線 */
.navbar-nav .nav-item:not(:last-child) .navbar-text {
  border-right: 1px solid #495057;
  margin-right: 0.5rem;
}

/* Mobile menu toggle */
.navbar-toggler {
  border: none;
  padding: 0.25rem 0.5rem;
  font-size: 1.25rem;
  background: transparent;
}

.navbar-toggler:focus {
  box-shadow: none;
}

.navbar-toggler-icon {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%28255, 255, 255, 0.75%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
  transition: transform 0.3s ease;
}

.navbar-toggler.active .navbar-toggler-icon {
  transform: rotate(90deg);
}

/* Mobile menu */
.mobile-menu {
  position: fixed;
  top: 0;
  right: -100%;
  width: 280px;
  height: 100vh;
  background-color: #343a40;
  z-index: 1050;
  transition: right 0.3s ease;
  overflow-y: auto;
}

.mobile-menu.show {
  right: 0;
}

.mobile-menu-content {
  padding: 1rem 0;
  height: 100%;
}

/* Mobile user info */
.mobile-user-info {
  padding: 1rem;
  border-bottom: 1px solid #495057;
  margin-bottom: 1rem;
}

.user-section {
  color: #fff;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.update-section {
  color: #6c757d;
  font-size: 0.85rem;
}

.update-section i {
  color: #28a745;
}

/* Mobile navigation links */
.mobile-nav-links {
  padding: 0;
}

.mobile-nav-link {
  display: block;
  color: #adb5bd;
  text-decoration: none;
  padding: 0.75rem 1rem;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: 1px solid #495057;
  position: relative;
}

.mobile-nav-link:hover {
  color: #fff;
  background-color: #495057;
  text-decoration: none;
  transform: translateX(5px);
}

.mobile-nav-link.router-link-active {
  color: #fff;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  font-weight: 600;
  transform: translateX(8px);
  box-shadow: inset 4px 0 0 #fff;
}

/* Mobile active state icon animation */
.mobile-nav-link.router-link-active i {
  animation: bounce 0.6s ease-in-out;
}

/* 移動端點擊動畫 */
@keyframes mobileClickAnimation {
  0% {
    transform: translateX(8px) scale(1);
    background-color: #007bff;
  }
  50% {
    transform: translateX(12px) scale(1.02);
    background-color: #0056b3;
  }
  100% {
    transform: translateX(8px) scale(1);
    background-color: #007bff;
  }
}

/* Mobile menu overlay */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.mobile-menu-overlay.show {
  opacity: 1;
  visibility: visible;
}

/* Responsive adjustments */
@media (max-width: 991.98px) {
  /* 只在小於桌面版時調整 container-fluid 佈局 */
  .navbar .container-fluid {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

@media (max-width: 575.98px) {
  .navbar-brand {
    font-size: 1rem;
    max-width: calc(100vw - 80px); /* 為小螢幕調整，留空間給漢堡按鈕 */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .mobile-menu {
    width: 100vw;
    right: -100vw;
  }
}

@media (max-width: 480px) {
  .navbar-brand {
    font-size: 0.9rem;
    max-width: calc(100vw - 70px); /* 更小螢幕的調整 */
  }
}

@media (max-width: 767.98px) {
  .mobile-menu {
    width: 320px;
  }
}

/* Ensure proper z-index stacking */
.navbar {
  z-index: 1030;
}
</style>